// services/titleService.ts
import { Page, PagesData, TitleServiceConfig } from './page';

class TitleService {
  private pagesKey: string;
  private defaultTitle: string;
  private pages: Page[];
  private urlToNameMap: Map<string, string>;

  constructor(config: TitleServiceConfig = { pagesKey: 'pages', defaultTitle: 'Application' }) {
    this.pagesKey = config.pagesKey;
    this.defaultTitle = config.defaultTitle || 'Application';
    this.pages = [];
    this.urlToNameMap = new Map();
    this.loadPagesConfig();
  }

  // 加载页面配置
  private loadPagesConfig(): void {
    try {
      const pagesData = localStorage.getItem(this.pagesKey);
      this.pages = pagesData ? (JSON.parse(pagesData) as PagesData) : [];
      this.buildUrlToNameMap();
    } catch (error) {
      console.error('Failed to load pages config:', error);
      this.pages = [];
      this.urlToNameMap.clear();
    }
  }

  // 构建URL到名称的映射
  private buildUrlToNameMap(): void {
    this.urlToNameMap.clear();
    
    this.pages.forEach((page: Page) => {
      if (page.pageUrl && page.pageName) {
        this.urlToNameMap.set(page.pageUrl, page.pageName);
      }
      
      // 同时处理group中的页面
      if (page.group?.page) {
        page.group.page.forEach((groupPage: Page) => {
          if (groupPage.pageUrl && groupPage.pageName) {
            this.urlToNameMap.set(groupPage.pageUrl, groupPage.pageName);
          }
        });
      }
    });
  }

  // 根据路径获取标题
  public getTitle(path: string): string {
    return this.getTitleFromPagesConfig(path) || this.defaultTitle;
  }

  // 从pages配置中获取标题
  private getTitleFromPagesConfig(path: string): string | null {
    // 精确匹配
    const exactMatch = this.urlToNameMap.get(path);
    if (exactMatch) {
      return exactMatch;
    }
    
    // 模糊匹配（处理带参数的路由）
    const fuzzyMatch = this.findFuzzyMatch(path);
    if (fuzzyMatch) {
      return fuzzyMatch;
    }
    
    return null;
  }

  // 修复的模糊匹配逻辑 - 避免迭代器问题
  private findFuzzyMatch(currentPath: string): string | null {
    // 方法1: 使用 forEach 替代迭代器
    let matchedTitle: string | null = null;
    
    this.urlToNameMap.forEach((name: string, url: string) => {
      if (this.isPathMatch(url, currentPath)) {
        matchedTitle = name;
      }
    });
    
    return matchedTitle;
  }

  // 方法2: 使用 Array.from 转换
  private findFuzzyMatchAlternative(currentPath: string): string | null {
    const entries = Array.from(this.urlToNameMap.entries());
    
    for (const [url, name] of entries) {
      if (this.isPathMatch(url, currentPath)) {
        return name;
      }
    }
    
    return null;
  }

  // 路径匹配逻辑
  private isPathMatch(configPath: string, currentPath: string): boolean {
    // 简单实现：如果配置路径是当前路径的前缀，则匹配
    return currentPath.startsWith(configPath) && configPath !== '/';
  }

  // 获取所有页面配置
  public getAllPages(): Page[] {
    return [...this.pages];
  }

  // 根据pageId获取页面信息
  public getPageById(pageId: number): Page | undefined {
    return this.pages.find(page => page.pageId === pageId);
  }

  // 根据URL获取页面信息
  public getPageByUrl(url: string): Page | undefined {
    return this.pages.find(page => page.pageUrl === url) || 
           this.pages.find(page => 
             page.group?.page?.some((p: Page) => p.pageUrl === url)
           );
  }

  // 重新加载配置（用于监听storage变化时）
  public reloadConfig(): void {
    this.loadPagesConfig();
  }

  // 设置默认标题
  public setDefaultTitle(title: string): void {
    this.defaultTitle = title;
  }
}

export const titleService = new TitleService();